import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CardMedia from '@mui/material/CardMedia';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import AppDateTimePicker from 'src/common/components/form/AppDateTimePicker';
import dayjs, { toDayjs } from 'src/helpers/dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import IconPickerTriggerButton from 'src/common/components/form/IconPickerTriggerButton';
import LocalizedTextField from 'src/common/components/form/LocalizedTextField';
import AppIconPickerModal from 'src/common/components/modals/AppIconPickerModal';
import MediaManagerModal from 'src/common/components/modals/MediaManagerModal';
import { LanguageKeys } from 'src/common/constants/languages';
import LanguageFlagSwitcher from 'src/pages/Dashboard/System/Pages/components/LanguageFlagSwitcher';
import useDeferredFileUsage from 'src/hooks/useDeferredFileUsage';
import { UploadedFile } from 'src/interfaces/FileManager';
import {
  BUTTON_BLOCK_THEME_COLORS,
  isButtonBlockHexColor,
} from 'src/helpers/buttonBlockStyleUtils';
import {
  IButtonLinkBlock,
  ICalendarBlock,
  ICountdownBlock,
  ILogoBlock,
  IPageBlock,
} from 'src/interfaces/PageBlock';

interface Props {
  block: IPageBlock;
  selectedEditorLang: LanguageKeys;
  setSelectedEditorLang: (lang: LanguageKeys) => void;
  contentKey: string;
  pageContentId?: string;
  onChange: (patch: Partial<IPageBlock>) => void;
}

const localizedInputSlotProps = (
  lang: LanguageKeys,
  onLangChange: (lang: LanguageKeys) => void,
) => ({
  input: {
    endAdornment: <LanguageFlagSwitcher value={lang} onChange={onLangChange} />,
  },
});

interface HeroButtonBlockFieldsProps {
  block: IButtonLinkBlock;
  selectedEditorLang: LanguageKeys;
  setSelectedEditorLang: (lang: LanguageKeys) => void;
  onChange: (patch: Partial<IPageBlock>) => void;
}

function HeroButtonBlockFields({
  block: btn,
  selectedEditorLang,
  setSelectedEditorLang,
  onChange,
}: HeroButtonBlockFieldsProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const isCustomColor = Boolean(btn.buttonColor && isButtonBlockHexColor(btn.buttonColor));
  const colorSelectValue = isCustomColor ? '__custom__' : (btn.buttonColor ?? '');

  return (
    <Box sx={{ mt: 1, display: 'grid', gap: 1.5 }}>
      <TextField
        size="small"
        fullWidth
        label={t('system.pages.hero.buttonLabel')}
        value={btn.label[selectedEditorLang] ?? ''}
        onChange={e =>
          onChange({
            label: { ...btn.label, [selectedEditorLang]: e.target.value },
          } as Partial<IButtonLinkBlock>)
        }
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Tooltip
                  title={
                    btn.icon
                      ? t('system.pages.hero.buttonChangeIcon')
                      : t('system.pages.hero.buttonSelectIcon')
                  }
                >
                  <IconPickerTriggerButton
                    edge="start"
                    value={btn.icon}
                    onOpen={() => setIconPickerOpen(true)}
                    aria-label={
                      btn.icon
                        ? t('system.pages.hero.buttonChangeIcon')
                        : t('system.pages.hero.buttonSelectIcon')
                    }
                    sx={{ ml: -0.5 }}
                  />
                </Tooltip>
              </InputAdornment>
            ),
            endAdornment: (
              <LanguageFlagSwitcher
                value={selectedEditorLang}
                onChange={setSelectedEditorLang}
              />
            ),
          },
        }}
      />
      <AppIconPickerModal
        open={iconPickerOpen}
        onClose={() => setIconPickerOpen(false)}
        value={btn.icon}
        onSelect={icon => onChange({ icon } as Partial<IButtonLinkBlock>)}
      />
      <LocalizedTextField
        label={t('system.pages.hero.buttonHref')}
        value={btn.href}
        onChange={href => onChange({ href } as Partial<IButtonLinkBlock>)}
        editorLang={selectedEditorLang}
        onEditorLangChange={setSelectedEditorLang}
        size="small"
        margin="none"
      />
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <FormControl size="small" sx={{ flex: '1 1 140px', minWidth: 120 }}>
          <InputLabel>{t('system.pages.hero.buttonColor')}</InputLabel>
          <Select
            label={t('system.pages.hero.buttonColor')}
            value={colorSelectValue}
            onChange={e => {
              const value = e.target.value;
              if (value === '') {
                onChange({ buttonColor: undefined } as Partial<IButtonLinkBlock>);
              } else if (value === '__custom__') {
                onChange({
                  buttonColor:
                    btn.buttonColor && isButtonBlockHexColor(btn.buttonColor)
                      ? btn.buttonColor
                      : theme.palette.primary.main,
                } as Partial<IButtonLinkBlock>);
              } else {
                onChange({ buttonColor: value } as Partial<IButtonLinkBlock>);
              }
            }}
          >
            <MenuItem value="">{t('system.pages.hero.buttonColorDefault')}</MenuItem>
            {BUTTON_BLOCK_THEME_COLORS.map(colorKey => (
              <MenuItem key={colorKey} value={colorKey}>
                {t(`editor.iconSelector.colors.${colorKey}`)}
              </MenuItem>
            ))}
            <MenuItem value="__custom__">{t('system.pages.hero.buttonColorCustom')}</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ flex: '1 1 100px', minWidth: 100 }}>
          <InputLabel>{t('system.pages.hero.buttonVariant')}</InputLabel>
          <Select
            label={t('system.pages.hero.buttonVariant')}
            value={btn.variant ?? 'contained'}
            onChange={e =>
              onChange({
                variant: e.target.value as IButtonLinkBlock['variant'],
              })
            }
          >
            <MenuItem value="text">text</MenuItem>
            <MenuItem value="outlined">outlined</MenuItem>
            <MenuItem value="contained">contained</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ flex: '1 1 100px', minWidth: 100 }}>
          <InputLabel>{t('system.pages.hero.buttonSize')}</InputLabel>
          <Select
            label={t('system.pages.hero.buttonSize')}
            value={btn.size ?? 'large'}
            onChange={e => onChange({ size: e.target.value as IButtonLinkBlock['size'] })}
          >
            <MenuItem value="small">small</MenuItem>
            <MenuItem value="medium">medium</MenuItem>
            <MenuItem value="large">large</MenuItem>
          </Select>
        </FormControl>
      </Box>
      {isCustomColor ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            type="color"
            label={t('system.pages.hero.buttonColorCustom')}
            value={btn.buttonColor}
            onChange={e => onChange({ buttonColor: e.target.value } as Partial<IButtonLinkBlock>)}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ width: 88 }}
          />
          <TextField
            size="small"
            label={t('system.pages.hero.buttonColorHex')}
            value={btn.buttonColor ?? ''}
            onChange={e => {
              const next = e.target.value.trim();
              if (!next || isButtonBlockHexColor(next)) {
                onChange({
                  buttonColor: next || theme.palette.primary.main,
                } as Partial<IButtonLinkBlock>);
              }
            }}
            placeholder="#9C5A37"
            sx={{ flex: 1 }}
          />
        </Box>
      ) : null}
    </Box>
  );
}

export default function PageBlockEditorFields({
  block,
  selectedEditorLang,
  setSelectedEditorLang,
  contentKey,
  pageContentId,
  onChange,
}: Props) {
  const { t } = useTranslation();
  const [mediaOpen, setMediaOpen] = useState(false);
  const { addPendingFile } = useDeferredFileUsage({
    contentType: 'pageContent',
    contentId: pageContentId || contentKey,
    fieldName: `block-${block.id}`,
  });

  const handleImageSelect = (file: UploadedFile) => {
    if (file.uploadEndpoint !== 'image') return;
    onChange({
      imageUrl: file.fileUrl,
      imageFileId: file.id,
    } as Partial<ILogoBlock>);
    addPendingFile(file.id);
  };

  switch (block.type) {
    case 'logo': {
      const logo = block as ILogoBlock;
      return (
        <Box sx={{ mt: 1, display: 'grid', gap: 1.5 }}>
          {logo.imageUrl ? (
            <CardMedia
              component="img"
              src={logo.imageUrl}
              sx={{ maxHeight: 120, objectFit: 'contain', alignSelf: 'flex-start' }}
            />
          ) : null}
          <Button variant="outlined" size="small" onClick={() => setMediaOpen(true)}>
            {t('system.pages.hero.selectLogo')}
          </Button>
          <TextField
            size="small"
            fullWidth
            label={t('editor.imageEdit.alt')}
            value={logo.alt[selectedEditorLang] ?? ''}
            onChange={e =>
              onChange({
                alt: { ...logo.alt, [selectedEditorLang]: e.target.value },
              } as Partial<ILogoBlock>)
            }
            slotProps={localizedInputSlotProps(selectedEditorLang, setSelectedEditorLang)}
          />
          <TextField
            size="small"
            type="number"
            label={t('system.pages.hero.maxHeight')}
            value={logo.maxHeight ?? 200}
            onChange={e =>
              onChange({ maxHeight: Number(e.target.value) || 200 } as Partial<ILogoBlock>)
            }
          />
          <MediaManagerModal
            open={mediaOpen}
            onClose={() => setMediaOpen(false)}
            onSelect={handleImageSelect}
            mode="select"
            endpoint="image"
            title={t('system.pages.hero.selectLogo')}
            showUploadButton
          />
        </Box>
      );
    }
    case 'calendar':
    case 'countdown': {
      const dateBlock = block as ICalendarBlock | ICountdownBlock;
      const useEdition = dateBlock.useEditionDate !== false;
      return (
        <Box sx={{ mt: 1, display: 'grid', gap: 1.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={useEdition}
                onChange={e =>
                  onChange({
                    useEditionDate: e.target.checked,
                    ...(e.target.checked ? { customDate: undefined } : {}),
                  })
                }
              />
            }
            label={t('system.pages.hero.useEditionDate')}
          />
          {!useEdition ? (
            <AppDateTimePicker
              label={t('system.pages.hero.customDate')}
              value={dateBlock.customDate ? toDayjs(dateBlock.customDate) : null}
              onChange={value =>
                onChange({
                  customDate: value ? dayjs(value).toISOString() : undefined,
                })
              }
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          ) : null}
          {block.type === 'countdown' ? (
            <Box>
              <FormLabel sx={{ display: 'block', mb: 0.5 }}>
                {t('system.pages.hero.countdownUnits')}
              </FormLabel>
              {(['days', 'hours', 'minutes', 'seconds'] as const).map((unit, i) => {
                const countdown = block as ICountdownBlock;
                const renderMap = countdown.renderMap ?? [true, true, false, false];
                return (
                  <FormControlLabel
                    key={unit}
                    control={
                      <Checkbox
                        checked={renderMap[i] ?? false}
                        onChange={e => {
                          const next = [...renderMap] as [boolean, boolean, boolean, boolean];
                          next[i] = e.target.checked;
                          onChange({ renderMap: next } as Partial<ICountdownBlock>);
                        }}
                      />
                    }
                    label={t(`timers.flipClockLabels.${unit}`)}
                  />
                );
              })}
            </Box>
          ) : null}
        </Box>
      );
    }
    case 'button':
      return (
        <HeroButtonBlockFields
          block={block as IButtonLinkBlock}
          selectedEditorLang={selectedEditorLang}
          setSelectedEditorLang={setSelectedEditorLang}
          onChange={onChange}
        />
      );
    default:
      return null;
  }
}
